import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Videos
import smVideo from '../assets/0916.mp4';
import lgVideo from '../assets/0916.mp4';

// Product Images
import goldImage from '../assets/Images/81.jpg';
import silverImage from '../assets/Images/31.jpeg';
import preciousImage from '../assets/Images/19.jpeg';
import semiPreciousImage from '../assets/Images/13.jpeg';

// Gallery Images
import gallery1 from '../assets/Images/75.jpeg';
import gallery2 from '../assets/Images/18.jpeg';
import gallery3 from '../assets/Images/25.jpeg';
import gallery4 from '../assets/Images/78.jpeg';
import gallery5 from '../assets/Images/52.jpeg';
import gallery6 from '../assets/Images/3.jpeg';
import gallery7 from '../assets/Images/29.jpeg';
import gallery8 from '../assets/Images/40.jpeg';
import gallery9 from '../assets/Images/11.jpeg';
import gallery10 from '../assets/Images/80.jpeg';
import gallery11 from '../assets/Images/19.jpeg';
import gallery12 from '../assets/Images/47.jpeg';

// Kandy Section Images
import kandy1 from '../assets/Images/115.jpeg';
import kandy2 from '../assets/Home/1.jpeg';
import kandy3 from '../assets/Home/2.jpeg';
import kandy4 from '../assets/Home/3.jpeg';
import kandy5 from '../assets/Images/118.jpeg';

// Animation variants (unchanged)
const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const slideLeft = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const scaleUp = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: 'easeOut' } },
};

const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: { staggerChildren: 0.2 },
    },
};

const accordionItem = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const Home = () => {
    const { t } = useTranslation('home');

    const [activeIndex, setActiveIndex] = useState(null);

    const toggleService = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://elfsightcdn.com/platform.js';
        script.async = true;
        document.body.appendChild(script);

        script.onload = () => console.log('Elfsight script loaded successfully');
        script.onerror = () => console.error('Failed to load Elfsight script');

        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, []);

    const services = [
        { key: 'ethicalSourcing' },
        { key: 'bespokeDesign' },
        { key: 'expertCraftsmanship' },
        { key: 'globalExpertise' },
        { key: 'trustedLegacy' },
    ];

    const achievements = [
        { key: 'goldProducts', count: "500+" },
        { key: 'silverProducts', count: "300+" },
        { key: 'gemStones', count: "1000+" },
        { key: 'customers', count: "5000+" },
    ];

    const products = [
        { img: goldImage, key: 'gold' },
        { img: silverImage, key: 'silver' },
        { img: preciousImage, key: 'precious' },
        { img: semiPreciousImage, key: 'semiPrecious' },
    ];

    const galleryImages = [
        { src: gallery1, altKey: 'gallery1' },
        { src: gallery2, altKey: 'gallery2' },
        { src: gallery3, altKey: 'gallery3' },
        { src: gallery4, altKey: 'gallery4' },
        { src: gallery5, altKey: 'gallery5' },
        { src: gallery6, altKey: 'gallery6' },
        { src: gallery7, altKey: 'gallery7' },
        { src: gallery8, altKey: 'gallery8' },
        { src: gallery9, altKey: 'gallery9' },
        { src: gallery10, altKey: 'gallery10' },
        { src: gallery11, altKey: 'gallery11' },
        { src: gallery12, altKey: 'gallery12' },
    ];

    const kandyImages = [
        { src: kandy1, altKey: 'kandy1' },
        { src: kandy2, altKey: 'kandy2' },
        { src: kandy3, altKey: 'kandy3' },
        { src: kandy4, altKey: 'kandy4' },
        { src: kandy5, altKey: 'kandy5' },
    ];

    const kandySliderSettings = {
        dots: true,
        infinite: true,
        speed: 800,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 4000,
        arrows: false,
        appendDots: (dots) => (
            <div className="mt-4">
                <ul className="flex justify-center gap-2"> {dots} </ul>
            </div>
        ),
        customPaging: () => (
            <div className="w-3 h-3 rounded-full bg-white hover:bg-[#BF943A] transition"></div>
        ),
    };

    const mobileSettings = {
        dots: true,
        infinite: true,
        speed: 800,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
        appendDots: (dots) => (
            <div className="mt-6">
                <ul className="flex justify-center gap-2"> {dots} </ul>
            </div>
        ),
        customPaging: () => (
            <div className="w-2 h-2 rounded-full bg-white opacity-70 hover:opacity-100 transition"></div>
        ),
        prevArrow: (
            <div className="slick-prev !left-2 !w-14 !h-14 !flex !items-center !justify-center !bg-white/80 !rounded-full hover:!bg-[#BF943A] !text-black hover:!text-white transition-all !shadow-lg z-20">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
                </svg>
            </div>
        ),
        nextArrow: (
            <div className="slick-next !right-2 !w-14 !h-14 !flex !items-center !justify-center !bg-white/80 !rounded-full hover:!bg-[#BF943A] !text-black hover:!text-white transition-all !shadow-lg z-20">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                </svg>
            </div>
        ),
    };

    const tabSettings = {
        dots: true,
        infinite: true,
        speed: 800,
        slidesToShow: 2,
        slidesToScroll: 1,
        arrows: false,
        appendDots: (dots) => (
            <div className="mt-6">
                <ul className="flex justify-center gap-2"> {dots} </ul>
            </div>
        ),
        customPaging: () => (
            <div className="w-2 h-2 rounded-full bg-white opacity-70 hover:opacity-100 transition"></div>
        ),
    };

    return (
        <div className="min-h-screen bg-[#1F1F1F]">
            {/* Hero Section */}
            <div className="relative flex h-[95vh] w-full items-center justify-center font-redhat lg:h-screen">
                <video
                    src={smVideo}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="absolute inset-0 h-full w-full object-cover block sm:hidden"
                />
                <video
                    src={lgVideo}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="absolute inset-0 h-full w-full object-cover hidden sm:block"
                />
                <div className="absolute inset-0 bg-black/60" />
                <div className="container relative z-10 flex items-center justify-center pt-[100px]">
                    <div className="w-[90%] text-center md:w-[75%]">
                        <motion.h1
                            variants={textVariants}
                            initial="hidden"
                            animate="visible"
                            className="text-[45px] font-semibold leading-[60px] text-white md:text-[55px] md:leading-[80px] lg:text-[70px] font-copperplate"
                        >
                            {t('hero.title')}
                        </motion.h1>
                        <motion.div
                            variants={textVariants}
                            initial="hidden"
                            animate="visible"
                            className="my-5 text-[18px] font-medium leading-6 md:text-[20px] lg:text-[25px] lg:leading-8"
                        >
                            <span className="text-[#BF943A]">
                                {t('hero.subtitle.highlight')}
                            </span>
                            <span className="text-white">
                                {' '}{t('hero.subtitle.part2')}
                            </span>
                        </motion.div>
                        <motion.div
                            variants={slideLeft}
                            initial="hidden"
                            animate="visible"
                            className="mt-10 flex justify-center gap-4"
                        >
                            <a
                                href="/our-products"
                                className="w-[180px] rounded-full bg-white py-2 text-[14px] font-bold text-black md:text-[16px] lg:text-[18px] hover:bg-gray-200 transition"
                            >
                                {t('hero.buttons.explore')}
                            </a>
                            <a
                                href="/contact-us"
                                className="w-[180px] rounded-full bg-[#BF943A] py-2 text-[14px] font-bold text-black md:text-[16px] lg:text-[18px] hover:bg-[#A07A2F] transition"
                            >
                                {t('hero.buttons.contact')}
                            </a>
                        </motion.div>
                        {/* decorative svgs unchanged */}
                    </div>
                </div>
            </div>

            {/* Marino Gems and Jewelleries Kandy Section */}
            <section className="bg-[#1F1F1F] py-12">
                <div className="container font-redhat text-white">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="flex items-center gap-5">
                        <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                        <h1 className="text-[15px] font-semibold uppercase md:text-[30px] lg:text-[40px]">
                            {t('kandy.title')}
                        </h1>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="mt-2 justify-between lg:flex">
                        <div className="w-full font-semibold text-[#AEAEAE] lg:w-[50%] lg:text-[18px]">
                            {t('kandy.subtitle')}
                        </div>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="my-6 h-px w-full bg-[#D7D7D7] lg:my-12"></motion.div>

                    <div className="justify-between gap-14 lg:flex">
                        {/* Kandy Swiper */}
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={scaleUp} className="relative mb-10 h-[500px] w-full lg:mb-0 lg:h-[650px] lg:w-[40%] rounded-lg overflow-hidden">
                            <Slider {...kandySliderSettings}>
                                {kandyImages.map((image, index) => (
                                    <div key={index} className="h-full">
                                        <img
                                            src={image.src}
                                            alt={t(`kandy.images.${image.altKey}`)}
                                            className="h-full w-full object-cover"
                                            onError={(e) => { e.target.src = 'https://via.placeholder.com/400x550?text=Image+Not+Found'; }}
                                        />
                                    </div>
                                ))}
                            </Slider>
                        </motion.div>

                        {/* Services Accordion */}
                        <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} className="w-full space-y-4 lg:w-[60%]">
                            {services.map((service, index) => (
                                <motion.div
                                    key={index}
                                    variants={accordionItem}
                                    className="cursor-pointer rounded-lg border border-gray-600 bg-[#1a1a1a] p-4 transition hover:border-white"
                                    onClick={() => toggleService(index)}
                                    role="button"
                                    aria-expanded={activeIndex === index}
                                    aria-controls={`service-${index}`}
                                >
                                    <h2 className="flex items-center justify-between text-[16px] font-semibold lg:text-[20px]">
                                        {t(`services.items.${service.key}.title`)}
                                        <span className="text-[25px]">
                                            {activeIndex === index ? '−' : '+'}
                                        </span>
                                    </h2>
                                    {activeIndex === index && (
                                        <p id={`service-${index}`} className="mt-2 text-[14px] text-[#C2C2C2] lg:text-[16px]">
                                            {t(`services.items.${service.key}.desc`)}
                                        </p>
                                    )}
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Our Achievements Section */}
            <section className="bg-[#1F1F1F]">
                <div className="container relative font-redhat text-white">
                    {/* decorative svgs unchanged */}
                    <div className="h-auto">
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={scaleUp} className="relative z-10 mt-12 rounded-3xl bg-black bg-gradient-to-r from-[#000000] to-[#240F01] py-10">
                            <h1 className="bg-gradient-to-r from-[#BF943A] to-white bg-clip-text text-center text-[30px] font-semibold text-transparent lg:text-[48px]">
                                {t('achievements.title')}
                            </h1>
                            <div className="mt-10 grid grid-cols-1 gap-8 text-center md:grid-cols-4">
                                {achievements.map((item, index) => (
                                    <div key={index} className="flex flex-col items-center gap-2">
                                        <div className="text-[24px] font-semibold">{t(`achievements.items.${item.key}.title`)}</div>
                                        <div className="h-px w-[150px] bg-[#D7D7D7]"></div>
                                        <div className="text-[48px] font-bold text-[#BF943A]">{item.count}</div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Our Products Section */}
            <section className="bg-[#1F1F1F] py-12">
                <div className="container relative font-redhat text-white">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="flex items-center gap-5">
                        <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                        <h1 className="text-[25px] font-semibold uppercase md:text-[30px] lg:text-[40px]">
                            {t('products.title')}
                        </h1>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="mt-2 justify-between lg:flex">
                        <div className="w-full text-[12px] font-semibold text-[#AEAEAE] md:text-[16px] lg:w-[60%] lg:text-[18px]">
                            {t('products.subtitle')}
                        </div>
                        <div className="mt-5 w-full justify-end lg:mt-0 lg:flex lg:w-[50%]">
                            <a
                                href="/our-products"
                                className="flex w-auto items-center justify-center gap-2 rounded-full bg-white px-5 py-2 text-[18px] font-semibold text-black lg:text-[20px] hover:bg-gray-200 transition"
                            >
                                {t('products.viewMore')}
                            </a>
                        </div>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="my-6 h-px w-full bg-[#D7D7D7] lg:my-12"></motion.div>

                    {/* Desktop Grid */}
                    <div className="mt-5 hidden grid-cols-1 gap-8 lg:grid lg:grid-cols-4">
                        {products.map((item, index) => (
                            <motion.div key={index} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={scaleUp}
                                className="group relative h-[350px] w-full rounded-lg border border-gray-600 bg-[#1a1a1a] shadow-lg transition hover:border-[#BF943A]">
                                <img
                                    src={item.img}
                                    alt={t(`products.items.${item.key}.title`)}
                                    className="h-[200px] w-full rounded-t-lg object-cover transition-transform duration-500 group-hover:scale-105"
                                    onError={(e) => { e.target.src = 'https://via.placeholder.com/150x150?text=Image+Not+Found'; }}
                                />
                                <div className="flex h-[150px] flex-col items-center justify-between p-4">
                                    <h2 className="text-center text-[20px] font-semibold text-white">
                                        {t(`products.items.${item.key}.title`)}
                                    </h2>
                                    <p className="text-center text-[14px] text-[#C2C2C2]">
                                        {t(`products.items.${item.key}.desc`)}
                                    </p>
                                    <a
                                        href={item.link}
                                        className="flex h-[40px] w-[40px] items-center justify-center rounded-full border-[1.5px] border-[#BF943A] text-[14px] font-semibold text-[#BF943A] hover:bg-[#BF943A] hover:text-black transition"
                                    >
                                        {t('products.view')}
                                    </a>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Mobile & Tablet Sliders */}
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="block md:hidden">
                        <Slider {...mobileSettings}>
                            {products.map((item, index) => (
                                <div key={index} className="px-4">
                                    <motion.div variants={scaleUp} className="group relative h-[450px] w-full rounded-lg border border-gray-600 bg-[#1a1a1a] shadow-lg transition hover:border-[#BF943A]">
                                        <img src={item.img} alt={t(`products.items.${item.key}.title`)} className="h-[200px] w-full rounded-t-lg object-cover transition-transform duration-500 group-hover:scale-105" />
                                        <div className="flex h-[250px] flex-col items-center justify-between p-4">
                                            <h2 className="text-center text-[20px] font-semibold text-white">{t(`products.items.${item.key}.title`)}</h2>
                                            <p className="text-center text-[14px] text-[#C2C2C2]">{t(`products.items.${item.key}.desc`)}</p>
                                            <a href={item.link} className="flex h-[40px] w-[40px] items-center justify-center rounded-full border-[1.5px] border-[#BF943A] text-[14px] font-semibold text-[#BF943A] hover:bg-[#BF943A] hover:text-black transition">
                                                {t('products.view')}
                                            </a>
                                        </div>
                                    </motion.div>
                                </div>
                            ))}
                        </Slider>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="hidden md:block lg:hidden">
                        <Slider {...tabSettings}>
                            {products.map((item, index) => (
                                <div key={index} className="px-4">
                                    <motion.div variants={scaleUp} className="group relative h-[150px] w-full rounded-lg border border-gray-600 bg-[#1a1a1a] shadow-lg transition hover:border-[#BF943A]">
                                        <img src={item.img} alt={t(`products.items.${item.key}.title`)} className="h-[150px] w-full rounded-t-lg object-cover transition-transform duration-500 group-hover:scale-105" />
                                        <div className="flex h-[150px] flex-col items-center justify-between p-4">
                                            <h2 className="text-center text-[20px] font-semibold text-white">{t(`products.items.${item.key}.title`)}</h2>
                                            <p className="text-center text-[14px] text-[#C2C2C2]">{t(`products.items.${item.key}.desc`)}</p>
                                            <a href={item.link} className="flex h-[40px] w-[40px] items-center justify-center rounded-full border-[1.5px] border-[#BF943A] text-[14px] font-semibold text-[#BF943A] hover:bg-[#BF943A] hover:text-black transition">
                                                {t('products.view')}
                                            </a>
                                        </div>
                                    </motion.div>
                                </div>
                            ))}
                        </Slider>
                    </motion.div>
                </div>
            </section>

            {/* Gallery Section */}
            <section className="bg-[#1F1F1F] py-12">
                <div className="container relative font-redhat text-white">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="flex items-center gap-5">
                        <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                        <h1 className="text-[25px] font-semibold uppercase md:text-[30px] lg:text-[40px]">
                            {t('gallery.title')}
                        </h1>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="mt-2 w-full font-semibold text-[#AEAEAE] lg:w-[50%] lg:text-[18px]">
                        {t('gallery.subtitle')}
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="my-6 h-px w-full bg-[#D7D7D7] lg:my-12"></motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                    >
                        {galleryImages.map((image, index) => (
                            <motion.div
                                key={index}
                                variants={scaleUp}
                                className="group relative aspect-[4/3] min-h-[220px] overflow-hidden rounded-lg bg-[#1a1a1a] sm:min-h-0"
                            >
                                <img
                                    src={image.src}
                                    alt={t(`gallery.images.${image.altKey}`)}
                                    loading={index > 3 ? 'lazy' : 'eager'}
                                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                                />
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Customers Say */}
            <section className="bg-[#1F1F1F] py-12">
                <div className="container font-redhat text-white">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="flex items-center gap-5">
                        <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                        <h1 className="text-[20px] font-semibold uppercase md:text-[30px] lg:text-[40px]">
                            {t('testimonials.title')}
                        </h1>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="my-6 h-px w-full bg-[#D7D7D7] lg:my-12"></motion.div>

                    <div className="py-10 min-h-[300px]">
                        <div className="elfsight-app-03bbefaf-7540-4702-897c-1d1724c0fd87"></div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;